import { FC } from 'react';
import { useTranslation } from 'next-i18next';
import { cx } from '@emotion/css';
import { useFlags } from 'launchdarkly-react-client-sdk';

import { useAppNews } from '@phoenix/core/frontend/shared/feature/app-news/use-app-news';
import { EditorialCard } from '@phoenix/core/frontend/shared/ui/editorial-card/editorial-card';
import { containerQueries } from '@phoenix/design-system/frontend/ui/breakpoints/breakpoints';
import { OverviewSubsection } from '@phoenix/team-m/dashboard/frontend/ui/overview/overview-subsection';
import { DynamicLinkWrapper } from '@phoenix/team-m/dashboard/frontend/shared/dynamic-link-wrapper';

import { useAemNews } from './use-aem-news';

export const NewsAndUpdates: FC = () => {
  const { 'app-news': appNewsFlagEnabled, 'aem-news': aemNewsFlagEnabled } = useFlags();
  const { data: aemNews } = useAemNews();
  const { data: appNews } = useAppNews(appNewsFlagEnabled && !aemNewsFlagEnabled);
  const data = aemNews ?? appNews;
  const { t } = useTranslation();

  return data?.length ? (
    <OverviewSubsection headline={t('dashboard_news_headline')} dataTestId="news-and-updates-section">
      <div className="mt-8 grid gap-6 desktop:grid-cols-3">
        {data.map(({ header, contentText, link, image }, i) => (
          <DynamicLinkWrapper
            aria-label={`${header} (${t('shared:shared_a11y_hint_opensInNewTab')})`}
            key={`dynamic-link-news-article-${i}`}
            className={cx({ 'desktop:col-span-2': i === 0 && data.length !== 3 })}
            hyperLink={link?.toString()}
            allowReferrer={true}
          >
            <EditorialCard
              className="h-full !max-w-full"
              dataTestId={`news-and-updates-article-${i + 1}`}
              title={header}
              description={contentText ?? ''}
              image={{
                src: image?.url ?? 'home-worker.jpeg',
                alt: !image?.description ? undefined : image.description,
                isPhoenix: !image?.url,
                className: 'desktop:aspect-auto desktop:h-52',
                sizes: `(max-width: ${containerQueries.tablet}) 100vw, (max-width: ${containerQueries.desktop}) 50vw, 30vw`,
              }}
            />
          </DynamicLinkWrapper>
        ))}
      </div>
    </OverviewSubsection>
  ) : null;
};
